import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApplicationStore } from 'src/store/application';
import { useInsuranceStore } from 'src/store/insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useFormSteps } from '@features/onboarding/hooks/useFormSteps';
import { FormStepSections } from '@features/onboarding/enums/formSections.enum';
import { ONBOARDING_STEP_KEY } from '@constants/index';

import { Button } from '@components/ui/button';
import { FloatingInput } from '@components/ui/input-floating';
import { FloatingPlacesInput } from '@components/ui/input-places';
import { useGeneralStore } from 'src/store';
import { applicationService } from 'src/services/application';

export const HomePage = () => {
  const { siteConfig } = useGeneralStore();
  const [selectedPlace, setSelectedPlace] =
    useState<google.maps.places.PlaceResult | null>(null);

  const navigate = useNavigate();
  const {
    activeSubscriptionPlans,
    fetchActiveSubscriptionPlans,
    applications,
    getCurrentApplication,
    tier, acceptedTier,
  } = useApplicationStore();
  const { plans: allPlans } = useInsuranceStore();
  const { setCurrentStep, setActiveDentalVisionCategory, setAiHighlightedPlan, phqSubmitted } = useOnboardingStore();
  const formSteps = useFormSteps();
  const [isUnderReviewLocal, setIsUnderReviewLocal] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getApplicationState = async () => {
    setIsLoading(true);
    try {
      const applicationState = await applicationService.applicationState();
      setIsUnderReviewLocal(!!applicationState.phqUnderReviewAt);
      setIsRejected(!!applicationState.phqRejectedAt);
    } catch (error) {
      console.error('Failed to get application state:', error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getApplicationState();
    fetchActiveSubscriptionPlans();
    getCurrentApplication();
  }, [fetchActiveSubscriptionPlans, getCurrentApplication]);

  const dentalAndVisionCategories = useMemo(() => {
    const categories = allPlans
      .map((p) => p.category)
      .filter((c) => c && c !== 'Major Medical');
    const uniqueCategories = [...new Set(categories)];
    const dentalIndex = uniqueCategories.indexOf('Dental');
    if (dentalIndex > 0) {
      // Move Dental to the front
      const dentalCategory = uniqueCategories.splice(dentalIndex, 1);
      uniqueCategories.unshift(dentalCategory[0]);
    }
    return uniqueCategories;
  }, [allPlans]);

  const handleJumpTo = (sectionName: string, category?: string) => {
    const stepIndex = formSteps.findIndex((step) => step.sectionName === sectionName);
    if (stepIndex === -1) return;

    setCurrentStep(stepIndex);
    localStorage.setItem(ONBOARDING_STEP_KEY, stepIndex.toString());

    if (sectionName === FormStepSections.Dental && category) {
      const categoryIndex = dentalAndVisionCategories.indexOf(category);
      if (categoryIndex !== -1) {
        setActiveDentalVisionCategory(categoryIndex);
      }
    }
    navigate('/');
  };

  const getIconForPlan = (planCategory?: string) => {
    if (!planCategory) return '/icons/plus.svg';
    const category = planCategory.toLowerCase();
    if (category.includes('dental')) return '/icons/dental.svg';
    if (category.includes('vision')) return '/icons/vision.svg';
    if (category.includes('accident')) return '/icons/accident.svg';
    if (category.includes('hospital')) return '/icons/hospital.svg';
    return '/icons/plus.svg';
  };

  const displayedPlans = useMemo(() => {
    const planMap = new Map();

    // Process active subscriptions
    activeSubscriptionPlans.forEach((sub) => {
      const details = allPlans.find((p) => p.id === sub.planId);
      if (details) {
        planMap.set(details.id, {
          id: details.id,
          name: details.name || details.marketingName,
          status: 'Active',
          buttonText: 'PLAN DETAILS',
          icon: getIconForPlan(details.category),
          onClick: () => {
            setAiHighlightedPlan(details.uid);
            navigate(`/healthhub/your-plans#plan-${details.id}`);
            // handleJumpTo(
            //   details.category === 'Major Medical'
            //     ? FormStepSections.Medical
            //     : FormStepSections.Dental,
            //   details.category,
            // );
          },
        });
      }
    });

    // Process plans from current application to check for pending PHQ
    const currentApplication = applications[0];
    if (currentApplication?.plans) {
      currentApplication.plans.forEach((appPlan) => {
        const details = allPlans.find((p) => p.id === appPlan.id);
        // If it's a medical plan and not yet an active subscription, it needs PHQ.
        if (details && details.category === 'Major Medical' && !planMap.has(details.id)) {
          planMap.set(details.id, {
            id: details.id,
            name: details.name || details.marketingName,
            status:
              "You haven't completed your PHQ. Please complete it to receive your quote.",
            buttonText: 'COMPLETE PHQ',
            icon: getIconForPlan(details.category),
            onClick: () => handleJumpTo(FormStepSections.HealthQuestion),
          });
        }
      });
    }

    // TODO: Logic for 'Payment Due' status will be added here
    // For now, it is not implemented as there's no clear source for this status.

    return Array.from(planMap.values());
  }, [activeSubscriptionPlans, applications, allPlans, formSteps]);

  return (
    <div className="flex gap-8 max-lg:flex-col mt-5">
      <section className="flex-[2] flex flex-col rounded-3xl bg-profile-section-bg pb-5">
        <div className="bg-profile-header-bg overflow-hidden relative text-white pl-30 2xl:pl-32 rounded-t-3xl py-7 text-[1.5vw] tracking-wide max-md:text-2xl max-md:py-5">
          <p className="text-3xl 2xl:text-4xl">Your current plans</p>
          <img
            className="absolute top-1 left-4 -rotate-22 max-1xl:top-0 w-[104px] h-[104px]"
            src="/icons/currentplans.svg"
            alt="plans"
          />
        </div>
        <div className="flex flex-col px-5 mt-2.5 gap-4">
          {displayedPlans.length > 0 ? (
            displayedPlans.map((plan, index) => (
              <div
                key={plan.id || index}
                className="flex items-center max-m:flex-col max-m:items-start max-m:gap-7 justify-between px-4 py-5.5 border-b border-gray-200 last:border-b-0"
              >
                <div className="flex items-center gap-4">
                  <img src={plan.icon} alt="icn" className="w-18 max-1xl:w-16 -ml-[9px] mr-[9px]" />
                  <div className="flex flex-col">
                    <h3 className="text-2xl font-bold text-primary">
                      {plan.name}
                    </h3>
                    <p className="text-2xl text-wrap max-1xl:text-xl text-[#2f281a]">
                      <span className="font-bold">Status:</span> {plan.status}
                    </p>
                  </div>
                </div>
                <button
                  onClick={plan.onClick}
                  className="px-4 max-m:m-auto max-1xl:text-base min-w-fit py-2 bg-primary rounded-full text-white text-xl font-semibold"
                >
                  {plan.buttonText}
                </button>
              </div>
            ))
          ) : (
            <div className="text-center p-10 flex flex-col items-center">
              <p className="text-2xl mb-6 text-[#2f281a]">
                You have not selected any plans yet.
              </p>
              {!isUnderReviewLocal && !isRejected && !isLoading && (applications && applications.length > 0 ? (
                <button
                  onClick={() =>
                    handleJumpTo(
                      phqSubmitted ? FormStepSections.Medical : FormStepSections.HealthQuestion
                    )
                  }
                  className="px-4 max-m:m-auto max-1xl:text-base min-w-fit py-2 bg-primary rounded-full text-white text-xl font-semibold uppercase hover:bg-primary/90"
                >
                  {phqSubmitted ? 'Select Plan' : 'Complete PHQ'}
                </button>
              ) : (
                <>
                  <p className="text-2xl mb-6">
                    Please add at least one applicant, then proceed to plans.
                  </p>
                  <button
                    onClick={() => handleJumpTo(FormStepSections.Dependents)}
                    className="px-4 max-m:m-auto max-1xl:text-base min-w-fit py-2 bg-primary rounded-full text-white text-xl font-semibold uppercase hover:bg-primary/90"
                  >
                    Add Applicant
                  </button>
                </>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="flex-[1] flex flex-col gap-8">
        <section className="flex flex-col rounded-3xl bg-profile-section-bg pb-8 max-1xl:pb-5">
          <div className="bg-profile-header-bg overflow-hidden relative text-white pl-27 rounded-t-3xl py-7 text-[1.5vw] tracking-wide max-md:text-2xl max-md:py-5">
            <p className="text-3xl 2xl:text-4xl">Contact us</p>
            <img
              className="absolute top-3 left-0 -rotate-15 w-24"
              src="/icons/contact-us.svg"
              alt="contact us"
            />
          </div>
          <div className="flex flex-col max-lg:flex-row max-sm:flex-col max-lg:gap-2 max-md:min-h-27 max-md:items-center max-lg:justify-center px-8 mt-8 max-1xl:mt-5 max-xs:px-5 items-start">
            <p className="text-2xl mb-1 max-1xl:text-xl text-gray-700">
              By phone:{' '}
              <span className="font-bold">{siteConfig?.phones?.primary || ''}</span>
            </p>
            <p className="text-2xl max-1xl:text-xl text-gray-700">or</p>
            <button className="text-2xl max-1xl:text-xl text-primary hover:text-primary/80 text-left">
            <span className="text-2xl max-1xl:text-xl text-gray-700">By email: {' '}</span>
              <a className="font-semibold" href={`mailto:${siteConfig?.emails?.primary || ''}`}>{siteConfig?.emails?.primary || ''}</a>
            </button>
          </div>
        </section>

        {/* <section className="flex flex-col rounded-3xl bg-profile-section-bg pb-10">
          <div className="bg-profile-header-bg overflow-hidden relative text-white pl-25 max-1xl:pl-20 rounded-t-3xl py-8 text-[1.5vw] tracking-wide max-md:text-2xl max-md:py-5">
            <p className="max-1xl:text-2xl">Search for a provider</p>
            <img
              className="absolute top-4 -left-2 w-23 max-1xl:w-18 max-1xl:top-2 max-1xl:-left-0"
              src="/icons/search-icn.svg"
              alt="search"
            />
          </div>
          <div className="flex flex-col px-8 mt-8 max-1xl:mt-5  max-xs:px-5">
            <p className="text-2xl mb-6 max-1xl:text-xl">
              Finding a doctor or provider in our network is easy! Just enter
              their name and location below.
            </p>
            <div className="mt-5 flex flex-col gap-7.5 max-md:gap-4 max-md:mt-0">
              <div className="flex flex-col gap-7.5 max-md:flex-row max-md:gap-4">
                <FloatingInput
                  id="provider-name"
                  label="Provider Name"
                  type="text"
                  icon="/icons/search.svg"
                  inputClass="!py-5 max-md:!py-4"
                />
                <FloatingPlacesInput
                  id="location"
                  label="Enter Location"
                  type="text"
                  icon="/icons/locat.svg"
                  inputClass="!py-5 max-md:!py-4"
                  onPlaceSelect={setSelectedPlace}
                />
              </div>
              <div className="flex justify-end">
                <button className="bg-primary py-4 px-10 text-2xl max-1xl:text-xl max-1xl:py-3 text-white font-semibold rounded-full">
                  SEARCH
                </button>
              </div>
            </div>
          </div>
        </section> */}
      </div>
    </div>
  );
};
